// KPIs de tiendas para el Home y los vendedores del periodo por tienda (pestaña Equipo).
// La tabla de la pantalla Tiendas vive en `useStoresTable.ts`.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sellerKpisApi, storeKpisApi } from '@/api/services/retail-org/backoffice';
import type { SellerKpiRow } from '@/api/services/retail-org/backoffice';
import { inventoryLensApi } from '@/api/services/retail-catalog/backoffice';
import { StoreStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/StoreStatusEnum';
import { mxDay, mxTodayWindow } from '@/lib/dateHelpers';
import { reportPeriodWindow, type ReportPeriod } from '@/shared/patterns/PeriodBand';
import { useGoals, withDailyGoal, withPeriodGoal } from '../../lib/goals';
import { mergeStoreRoster, zoneNameById } from '../../lib/roster';
import { useRosterRetailer, useRosterStores, useRosterZones } from '../../lib/useRoster';
import { categoriesBySku } from '../lib/categoryMix';

const KEY = ['retail-admin', 'tiendas'] as const;

/** El periodo de la banda, o `today` para el Home, que mira solo el día. */
export type KpiPeriod = ReportPeriod | 'today';

/** La ventana del periodo; los bordes son de día completo, así que la clave no cambia hasta mañana. */
export function usePeriodWindow(period: KpiPeriod) {
    const today = mxDay(new Date().toISOString());
    return useMemo(
        () => (period === 'today' ? mxTodayWindow() : reportPeriodWindow(period)),
        // `today` rehace la ventana cuando cambia el día.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [period, today],
    );
}

/** Meta de la fila: la diaria en el Home, la mensual escalada en la banda de periodo. */
export function withKpiGoal<T extends { dailyGoalCents: number | null }>(
    rows: T[],
    idOf: (row: T) => string,
    goals: { targetId: string; monthCents: number; dayCents: number }[] | undefined,
    period: KpiPeriod,
): T[] {
    return period === 'today'
        ? withDailyGoal(rows, idOf, goals)
        : withPeriodGoal(rows, idOf, goals, period);
}

/** `rosterOptional`: si el padrón falla, lista solo las KPIs en vez de tumbar la pantalla (Home). */
export function useTiendas(period: KpiPeriod, { rosterOptional = false } = {}) {
    const periodWindow = usePeriodWindow(period);
    const kpis = useQuery({
        queryKey: [...KEY, 'list', periodWindow],
        queryFn: () => storeKpisApi.listStores(periodWindow),
        staleTime: 60_000,
    });
    const { retailerId, problemError } = useRosterRetailer();
    const stores = useRosterStores(retailerId);
    const zones = useRosterZones(retailerId);
    const goals = useGoals('STORE');

    const rosterFailed = problemError !== null || stores.isError;
    const skipRoster = rosterOptional && rosterFailed;

    const data = useMemo(() => {
        const roster = stores.data ?? (skipRoster ? [] : undefined);
        if (!kpis.data || !roster) return undefined;
        const rows = mergeStoreRoster(roster, kpis.data.items, zoneNameById(zones.data ?? []));
        // Sin padrón no se sabe qué tienda está activa: `null` en vez de un conjunto vacío.
        const activeStoreIds = stores.data
            ? new Set(
                  stores.data
                      .filter((store) => store.status === StoreStatusEnum.ACTIVE)
                      .map((store) => store.storeId),
              )
            : null;
        return {
            items: withKpiGoal(rows, (row) => row.storeId, goals.data?.goals, period),
            truncated: kpis.data.truncated,
            activeStoreIds,
        };
    }, [kpis.data, stores.data, skipRoster, zones.data, goals.data, period]);

    // Campos explícitos y no `{ ...query }`: el spread suscribe a todo el query y re-renderiza de más.
    const error = skipRoster ? kpis.error : (problemError ?? kpis.error ?? stores.error);
    return {
        data,
        error,
        goalsError: goals.error,
        isPending: error === null && (kpis.isPending || (!skipRoster && stores.isPending)),
        isError: error !== null,
        refetch: () => {
            void kpis.refetch();
            void stores.refetch();
        },
    };
}

/** Quiénes vendieron en el periodo, agrupados por tienda, con su meta del periodo. */
export function useStoreSellers(period: KpiPeriod, enabled = true) {
    const periodWindow = usePeriodWindow(period);
    const query = useQuery({
        queryKey: ['retail-admin', 'vendedores', 'list', periodWindow],
        queryFn: () => sellerKpisApi.listSellers(periodWindow),
        staleTime: 60_000,
        enabled,
    });
    const goals = useGoals('SELLER', enabled);

    const byStore = useMemo(() => {
        if (!query.data) return undefined;
        const rows = withKpiGoal(
            query.data.items,
            (row) => row.sellerId,
            goals.data?.goals,
            period,
        );
        const grouped = new Map<string, SellerKpiRow[]>();
        for (const row of rows) {
            if (row.storeId === null) continue;
            const bucket = grouped.get(row.storeId);
            if (bucket) bucket.push(row);
            else grouped.set(row.storeId, [row]);
        }
        return grouped;
    }, [query.data, goals.data, period]);

    return { byStore, isPending: query.isPending, isError: query.isError };
}

/**
 * Categoría de los SKUs de las ventas sin líneas, leída de la lente del catálogo. Sin permiso o con
 * la lente caída, esas ventas quedan en «Sin categoría».
 */
export function useLegacySaleCategories(skus: string[], canReadLens: boolean) {
    const query = useQuery({
        queryKey: [...KEY, 'catalog-categories'],
        queryFn: () => inventoryLensApi.getList({ period: '7d' }),
        enabled: canReadLens && skus.length > 0,
        staleTime: 5 * 60_000,
        retry: false,
    });
    return useMemo(
        () => (query.data ? categoriesBySku(query.data.items, skus) : undefined),
        [query.data, skus],
    );
}
